import { ReactNode, ReactElement } from 'react'
import { Button, Link } from '@chakra-ui/react'
import { darken } from '@chakra-ui/theme-tools'
import { useSpring, config } from 'react-spring'
import { useInView } from 'react-intersection-observer'

import CheckList from '@/components/CheckList'
import { AnimatedVStack } from '@/lib/springComponent'
import { logEvent } from '@/lib/stats/gtm'

import { ButtonData } from '@/features/Services/types'

interface Props {
  id?: string
  primaryColor: string
  secondaryColor: string
  title: ReactNode
  paragraph: ReactNode
  button?: ButtonData
  checkList?: string[]
  initialInView?: boolean
}

function SectionContent({
  id,
  primaryColor,
  secondaryColor,
  title,
  paragraph,
  button,
  checkList,
  initialInView,
}: Props): ReactElement {
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
    initialInView,
  })

  const spring = useSpring({
    to: {
      opacity: inView ? 1 : 0,
      transform: inView ? 'translate3d(0, 0px, 0)' : 'translate3d(0, 32px, 0)',
      // transform: inView ? 'scale(1)' : 'scale(0.4)',
      // clipPath: inView ? 'inset(0 0 0% 0)' : 'inset(0 0 100% 0)',
    },
    config: config.gentle,
  })

  return (
    <AnimatedVStack
      id={id}
      ref={ref}
      style={spring}
      spacing="4"
      sx={{
        p: 8,
        alignItems: {
          base: 'center',
          'lg+': 'flex-start',
        },
        maxWidth: {
          base: 'unset',
          'lg+': '541px',
        },
        width: '100%',
      }}
    >
      {title}
      {paragraph}
      {checkList ? (
        <CheckList
          labels={checkList}
          fillColor={secondaryColor}
          strokeColor={primaryColor}
        />
      ) : null}
      {button ? (
        <Link
          isExternal
          href={button.href}
          sx={{
            boxSizing: 'content-box',
            borderRadius: '8px',
            textDecoration: 'none !important',
          }}
        >
          <Button
            as="div"
            size="lg"
            sx={{
              height: 16,
              color: 'white',
              borderRadius: '8px',
              fontSize: {
                base: 'lg',
                md: '2xl',
              },
              backgroundColor: primaryColor,
              '&:hover': {
                backgroundColor: darken(primaryColor, 10),
              },
            }}
            onClick={() => {
              if (button.logEvent) {
                logEvent(button.logEvent)
              }
            }}
          >
            {button.label}
          </Button>
        </Link>
      ) : null}
    </AnimatedVStack>
  )
}

export default SectionContent
